import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'src/components/Link'
import Icon from 'src/icons'
import { css } from 'src/style/css'
import { Theme } from 'src/style/Theme'
import { FaRegCalendarAlt } from 'react-icons/fa'

// Keep dismissal across client navigation even if browser storage is unavailable.
const dismissedBanners = new Set<string>()

type AnnouncementBannerProps = {
    id: string
    heading: string
    text: string
    linkText: string
    closeLabel: string
    to: string
    startsAt?: string | null
    endsAt?: string | null
    onHeightChange: (height: number) => void
}

const AnnouncementBanner = ({
    id,
    heading,
    text,
    linkText,
    closeLabel,
    to,
    startsAt = null,
    endsAt = null,
    onHeightChange
}: AnnouncementBannerProps) => {
    const [visible, setVisible] = useState(false)
    const bannerRef = useRef<React.ElementRef<'div'>>(null)
    const storageKey = `announcement-banner:${id}:dismissed`

    useEffect(() => {
        const start = startsAt ? Date.parse(startsAt) : -Infinity
        const end = endsAt ? Date.parse(endsAt) : Infinity
        let timer: number

        const updateVisibility = () => {
            window.clearTimeout(timer)
            let dismissed = dismissedBanners.has(id)
            try {
                dismissed =
                    dismissed ||
                    window.sessionStorage.getItem(storageKey) === 'true'
            } catch {
                // Remember dismissal during navigation even if storage is blocked.
            }

            const now = Date.now()
            setVisible(!dismissed && now >= start && now < end)

            // Recheck at the next boundary, including while a page stays open.
            const nextBoundary = now < start ? start : end
            if (
                !dismissed &&
                Number.isFinite(nextBoundary) &&
                nextBoundary > now
            ) {
                timer = window.setTimeout(
                    updateVisibility,
                    Math.min(nextBoundary - now, 2147483647)
                )
            }
        }

        updateVisibility()
        document.addEventListener('visibilitychange', updateVisibility)
        window.addEventListener('pageshow', updateVisibility)

        return () => {
            window.clearTimeout(timer)
            document.removeEventListener('visibilitychange', updateVisibility)
            window.removeEventListener('pageshow', updateVisibility)
        }
    }, [id, storageKey, startsAt, endsAt])

    useEffect(() => {
        const banner = bannerRef.current
        if (!visible || !banner) {
            onHeightChange(0)
            return
        }

        const updateHeight = () =>
            onHeightChange(banner.getBoundingClientRect().height)
        updateHeight()
        const observer =
            typeof window.ResizeObserver === 'undefined'
                ? null
                : new window.ResizeObserver(updateHeight)
        observer?.observe(banner)
        window.addEventListener('resize', updateHeight)

        return () => {
            observer?.disconnect()
            window.removeEventListener('resize', updateHeight)
            onHeightChange(0)
        }
    }, [visible, heading, text, linkText, onHeightChange])

    const dismiss = () => {
        dismissedBanners.add(id)
        try {
            window.sessionStorage.setItem(storageKey, 'true')
        } catch {
            // Storage can be blocked; dismissal must still close the banner.
        }
        setVisible(false)
        onHeightChange(0)
    }

    if (!visible) return null

    return (
        <div
            ref={bannerRef}
            css={(th: Theme) => css`
                position: sticky;
                top: 0;
                z-index: ${th.zIndex.nav + 1};
                display: flex;
                align-items: center;
                justify-content: center;
                box-sizing: border-box;
                min-height: 48px;
                padding: 12px 60px;
                background: ${th.colors.primary};
                color: ${th.colors.white};
                font-size: ${th.fontSize.sm};
                line-height: 1.5;
                @media (max-width: ${th.mediaQuery.md}) {
                    padding: 12px 52px 12px 20px;
                }
            `}
        >
            <div
                css={(th: Theme) => css`
                    display: flex;
                    flex-wrap: wrap;
                    align-items: center;
                    justify-content: center;
                    gap: ${th.spacing.xs} ${th.spacing.md};
                    min-width: 0;
                    text-align: center;
                    @media (max-width: ${th.mediaQuery.md}) {
                        flex-direction: column;
                        align-items: flex-start;
                        text-align: left;
                    }
                `}
            >
                <FaRegCalendarAlt
                    aria-hidden="true"
                    css={(th: Theme) => css`
                        flex-shrink: 0;
                        font-size: ${th.fontSize.md};
                        @media (max-width: ${th.mediaQuery.md}) {
                            display: none;
                        }
                    `}
                />
                <span>
                    <strong>{heading}</strong> {text}
                </span>
                <span
                    aria-hidden="true"
                    css={(th: Theme) => css`
                        opacity: 0.5;
                        @media (max-width: ${th.mediaQuery.md}) {
                            display: none;
                        }
                    `}
                >
                    |
                </span>
                <Link
                    to={to}
                    css={(th: Theme) => css`
                        color: ${th.colors.white};
                        font-weight: ${th.fontWeight.bold};
                        text-decoration: underline;
                        text-underline-offset: 4px;
                        &:hover {
                            color: ${th.colors.white};
                            text-decoration-thickness: 2px;
                        }
                        &:focus-visible {
                            outline: 2px solid ${th.colors.white};
                            outline-offset: 4px;
                        }
                    `}
                >
                    {linkText}
                    <span aria-hidden="true"> →</span>
                </Link>
            </div>
            <button
                type="button"
                aria-label={closeLabel}
                onClick={dismiss}
                css={(th: Theme) => css`
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                    position: absolute;
                    top: 4px;
                    right: 4px;
                    width: 44px;
                    height: 44px;
                    padding: 0;
                    border: 0;
                    background: transparent;
                    color: ${th.colors.white};
                    font-size: ${th.fontSize.md};
                    cursor: pointer;
                    &:hover {
                        background: rgba(255, 255, 255, 0.15);
                    }
                    &:focus-visible {
                        outline: 2px solid ${th.colors.white};
                        outline-offset: -4px;
                    }
                `}
            >
                <Icon.Close aria-hidden="true" />
            </button>
        </div>
    )
}

export default AnnouncementBanner
